import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { authHeaders, Toast } from './listPageStyles';
import { StatusBadge } from './ui';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  DetailShell, Section, Grid, Icon, SubModal, SplitButton,
  FieldText, FieldSelect, FieldCustom,
} from './detail/DetailShell';
import { usePageActions } from '../contexts/PermissionContext';
import { parseFieldOptions, serializeFieldOptions } from './additionalFieldOptions';

// Ürün Özelleştirilmiş Alan — tam-sayfa detay (Personel pilotu deseni).
// Backend: /api/productadditionalfields  (Add POST / Update PUT / Delete PUT).
// GET-by-id YOK → tek kayıt önce navigation state'ten, yoksa OData listesinden id ile bulunur.
const API_BASE = '/api/productadditionalfields';
const LIST_ROUTE = '/product-additional-fields';

const DATA_TYPES = [
  { value: 1, label: 'Metin' },
  { value: 2, label: 'Sayı' },
  { value: 3, label: 'Tarih' },
  { value: 4, label: 'Evet/Hayır' },
  { value: 5, label: 'Listeden Seçim' }, // #726 seçenek listesinden değer seçimi
];
const REQUIRED_OPTIONS = [{ value: 'true', label: 'Evet' }, { value: 'false', label: 'Hayır' }];

// #743 options = [{ code, description }] (satır-satır seçenek). Geriye-uyum additionalFieldOptions.js'te.
const EMPTY_FORM = { textId: '', name: '', dataType: 1, isRequired: 'false', options: [] };
const OPT_INPUT = { flex: 1, minWidth: 0, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--dps-line, #d7dde5)', fontSize: 13.5, background: 'var(--dps-field-bg, #fff)' };

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}
function unwrap(payload) { return pick(payload, 'value', 'Data', 'data') ?? payload; }

function rowToForm(r) {
  return {
    textId: pick(r, 'textId', 'TextId') ?? '',
    name: pick(r, 'name', 'Name') ?? '',
    dataType: pick(r, 'dataType', 'DataType') ?? 1,
    isRequired: (pick(r, 'isRequired', 'IsRequired') ?? false) ? 'true' : 'false',
    options: parseFieldOptions(pick(r, 'options', 'Options')),
  };
}

export function ProductAdditionalFieldDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  const { canAdd, canEdit, canDelete } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;
  usePageMeta(isNew ? 'Yeni Özelleştirilmiş Alan' : 'Özelleştirilmiş Alan Detayı', 'Ürünler için özel alan tanımı.');

  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [audit, setAudit] = useState(null);

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };
  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));

  // #743 Seçenek satırı ekleme (kod + açıklama) — sipariş kalemi gibi tek tek eklenir.
  const [optDraft, setOptDraft] = useState({ code: '', description: '' });
  const addOption = () => {
    const code = optDraft.code.trim();
    const description = optDraft.description.trim();
    if (!code) { showToast('error', 'Seçenek kodu boş olamaz'); return; }
    if (form.options.some((o) => o.code.toLocaleLowerCase('tr') === code.toLocaleLowerCase('tr'))) {
      showToast('error', 'Bu seçenek kodu zaten ekli'); return;
    }
    set('options', [...form.options, { code, description: description || code }]);
    setOptDraft({ code: '', description: '' });
  };
  const removeOption = (index) => set('options', form.options.filter((_, i) => i !== index));

  // GET-by-id yok: önce navigation state'teki satır, yoksa OData listesini çekip id ile bul.
  const load = useCallback(async () => {
    if (isNew) { setLoading(false); return; }
    const stateRow = location.state?.row;
    if (stateRow && String(pick(stateRow, 'id', 'Id')) === String(id)) {
      setForm(rowToForm(stateRow));
      setLoading(false);
      // Liste $select audit alanlarini icermez -> tam kaydi OData'dan fire-and-forget cekip audit'i doldur.
      fetch(`${API_BASE}?$filter=Id eq ${id}`, { headers: authHeaders() })
        .then((res) => (res.ok ? res.json() : null))
        .then((body) => {
          const items = unwrap(body);
          const arr = Array.isArray(items) ? items : [];
          const rec = arr.find((r) => String(pick(r, 'id', 'Id')) === String(id)) || arr[0];
          if (rec) setAudit({ createdBy: pick(rec, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(rec, 'createdDate', 'CreatedDate'), updatedBy: pick(rec, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(rec, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
        })
        .catch(() => {});
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}?$filter=Id eq ${id}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Kayıt alınamadı (${res.status})`);
      const items = unwrap(await res.json());
      const arr = Array.isArray(items) ? items : [];
      const found = arr.find((r) => String(pick(r, 'id', 'Id')) === String(id)) || arr[0];
      if (!found) throw new Error('Kayıt bulunamadı');
      setForm(rowToForm(found));
      setAudit({ createdBy: pick(found, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(found, 'createdDate', 'CreatedDate'), updatedBy: pick(found, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(found, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
    } catch (err) {
      showToast('error', err.message || 'Kayıt alınamadı');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  useEffect(() => { load(); }, [load]);

  // Klonla: /…/new'e kopya veriyle gelindiyse formu doldur (kayıt OLUŞTURMA — kullanıcı Kaydet diyene kadar).
  useEffect(() => {
    if (isNew && location.state?.clone) {
      setForm(location.state.clone);
      setTried(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const buildPayload = () => {
    const dataType = Number(form.dataType) || 1;
    return {
      textId: form.textId.trim(),
      name: form.name.trim(),
      dataType,
      isRequired: form.isRequired === 'true',
      // #743 Seçenekler yalnız "Listeden Seçim" (5) tipinde [{code,description}] JSON'u olarak gönderilir.
      options: dataType === 5 ? serializeFieldOptions(form.options) : null,
    };
  };

  // andNew: kaydet ama listeye/detaya gitme — formu sıfırlayıp boş yeni kayda hazırlan
  // (önceki kayıt backend'e kaydedilmiş olur). Sadece yeni kayıt modunda anlamlı.
  const persist = async ({ andNew = false } = {}) => {
    setTried(true);
    if (!form.textId.trim()) { showToast('error', 'Kod boş olamaz'); return; }
    if (!form.name.trim()) { showToast('error', 'Ad boş olamaz'); return; }
    // #743 "Listeden Seçim" tipinde en az bir seçenek zorunlu.
    if (Number(form.dataType) === 5 && form.options.length === 0) {
      showToast('error', 'Listeden Seçim için en az bir seçenek girin'); return;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      const res = await fetch(`${API_BASE}/${isNew ? 'Add' : 'Update'}`, {
        method: isNew ? 'POST' : 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(isNew ? payload : { id, ...payload }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'message', 'Message') || (isNew ? 'Kayıt eklenemedi' : 'Kayıt güncellenemedi'));
      if (isNew) {
        if (andNew) {
          showToast('success', 'Özelleştirilmiş alan eklendi. Yeni kayda devam edebilirsiniz.');
          setForm(EMPTY_FORM);
          setTried(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        showToast('success', 'Özelleştirilmiş alan başarıyla eklendi');
        setTimeout(() => navigate(LIST_ROUTE), 400);
        return;
      }
      showToast('success', 'Özelleştirilmiş alan başarıyla güncellendi');
      setTimeout(() => navigate(LIST_ROUTE), 400);
    } catch (err) {
      showToast('error', err.message || 'İşlem sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => persist();

  // Klonla: mevcut kaydın form bilgilerini taşıyarak yeni-kayıt ekranını aç.
  // Hiçbir şey kaydedilmez; kullanıcı "Kaydet" diyene kadar yeni kayıt oluşmaz.
  const handleClone = () => {
    navigate(`${LIST_ROUTE}/new`, { state: { clone: { ...form } } });
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/Delete`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id }) });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'message', 'Message') || 'Kayıt silinemedi');
      showToast('success', 'Özelleştirilmiş alan başarıyla silindi');
      setTimeout(() => navigate(LIST_ROUTE), 400);
    } catch (err) {
      showToast('error', err.message || 'Silme işlemi sırasında hata oluştu');
      setSaving(false);
      setDeleteConfirm(false);
    }
  };

  const statusBadge = !isNew && (form.isRequired === 'true'
    ? <StatusBadge tone="ok">Zorunlu</StatusBadge>
    : <StatusBadge tone="neutral">Opsiyonel</StatusBadge>);

  const actions = (
    <>
      {!isNew && canDelete && <button className="dps-btn danger" onClick={() => setDeleteConfirm(true)} disabled={saving}><Icon name="x" size={16} /> Sil</button>}
      <button className="dps-btn ghost" onClick={() => navigate(LIST_ROUTE)} disabled={saving}>İptal</button>
      {canSave && (isNew ? (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Kaydet ve Yeni',
            desc: 'Kaydet, listeye dönme, yeni kayıt aç',
            icon: 'plus',
            onClick: () => persist({ andNew: true }),
          }]}
        />
      ) : (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Klonla',
            desc: 'Bu kaydı kopyalayıp yeni alan aç',
            icon: 'copy',
            onClick: handleClone,
          }]}
        />
      ))}
    </>
  );

  return (
    <DetailShell
      crumb={[{ label: 'Ürün Özelleştirilmiş Alan', to: LIST_ROUTE }, { label: isNew ? 'Yeni Özelleştirilmiş Alan' : 'Özelleştirilmiş Alan Detayı', current: true }]}
      status={statusBadge}
      actions={actions}
      audit={isNew ? null : audit}
      history={isNew ? null : { entityName: 'ProductAdditionalField', entityId: id }}
      loading={loading}
    >
      <div className="dps-form">
        <Section icon="box" title="Özelleştirilmiş Alan Bilgileri" subtitle="Ürünler için özel alan tanımı">
          <Grid cols={2}>
            <FieldText form={form} set={set} tried={tried} k="textId" label="Kod" req mono />
            <FieldText form={form} set={set} tried={tried} k="name" label="Ad" req />
            <FieldSelect form={form} set={set} k="dataType" label="Veri Tipi" options={DATA_TYPES} />
            <FieldSelect form={form} set={set} k="isRequired" label="Zorunlu" options={REQUIRED_OPTIONS} />
            {/* #743 Listeden Seçim: seçenekler satır-satır (kod + açıklama); değer olarak KOD saklanır. */}
            {Number(form.dataType) === 5 && (
              <FieldCustom label="Seçenekler (kod + açıklama)" span>
                <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                  <input
                    style={{ ...OPT_INPUT, flex: 1 }}
                    placeholder="Kod (örn. M)"
                    value={optDraft.code}
                    onChange={(e) => setOptDraft((d) => ({ ...d, code: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addOption(); } }}
                  />
                  <input
                    style={{ ...OPT_INPUT, flex: 2 }}
                    placeholder="Açıklama (örn. Mini)"
                    value={optDraft.description}
                    onChange={(e) => setOptDraft((d) => ({ ...d, description: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addOption(); } }}
                  />
                  <button type="button" className="dps-btn primary" onClick={addOption}>Ekle</button>
                </div>
                {form.options.length === 0 ? (
                  <div style={{ fontSize: 12.5, color: 'var(--dps-ink-3, #94a3b8)' }}>
                    Henüz seçenek eklenmedi. Kod ve açıklama girip “Ekle” deyin.
                  </div>
                ) : (
                  <table className="dps-table">
                    <thead><tr><th style={{ width: 140 }}>Kod</th><th>Açıklama</th><th className="dps-td-actions">İşlem</th></tr></thead>
                    <tbody>
                      {form.options.map((o, i) => (
                        <tr key={`${o.code}-${i}`}>
                          <td><strong style={{ fontFamily: 'ui-monospace, monospace' }}>{o.code}</strong></td>
                          <td>{o.description}</td>
                          <td className="dps-td-actions"><button type="button" className="dps-row-btn danger" onClick={() => removeOption(i)}>Çıkar</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {tried && form.options.length === 0 && (
                  <div className="dps-ff-hint" style={{ color: '#dc2626', marginTop: 6 }}>En az bir seçenek zorunlu.</div>
                )}
              </FieldCustom>
            )}
          </Grid>
        </Section>
      </div>

      {/* Silme onayı */}
      {deleteConfirm && (
        <SubModal
          title="Silme Onayı"
          maxWidth={440}
          onClose={() => setDeleteConfirm(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setDeleteConfirm(false)} disabled={saving}>İptal</button>
              <button className="dps-btn danger" onClick={handleDelete} disabled={saving}>{saving ? 'Siliniyor…' : 'Evet, Sil'}</button>
            </>
          )}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            <strong>“{form.textId}”</strong> kodlu özelleştirilmiş alanı silmek istediğinize emin misiniz?
          </p>
        </SubModal>
      )}

      <Toast toast={toast} />
    </DetailShell>
  );
}

export default ProductAdditionalFieldDetailPage;
